import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import {
  MdClose,
  MdCloudUpload,
  MdRestartAlt,
  MdWallpaper,
  MdOutlineImage,
} from "react-icons/md";
import styles from "./BackgroundUploadModal.module.css";
import { useBackgroundUploadModal } from "../../customHook/BackgroundUploadModal/useBackgroundUploadModal";

const BackgroundUploadModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  backgroundToEdit = null,
  defaultGender = "",
}) => {
  const {
    isEditMode,
    hasChanges,
    isFormValid,
    isFormDirty,
    formData,
    errors,
    previewUrl,
    zoomImage,
    setZoomImage,
    formRef,
    handleInputChange,
    handleInputBlur,
    handleImageChange,
    handleRemoveImage,
    handleSubmit,
    handleReset,
    handleCancel,
    handleBackdropClick,
  } = useBackgroundUploadModal({
    isOpen,
    onClose,
    onSubmit,
    backgroundToEdit,
    defaultGender,
  });

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (zoomImage) {
          setZoomImage(null);
          return;
        }
        if (!isLoading) {
          handleCancel();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, zoomImage, isLoading, setZoomImage, handleCancel]);

  if (!isOpen || typeof document === "undefined") return null;

  return createPortal(
    <div
      className={styles.modalOverlay}
      onClick={handleBackdropClick}
    >
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="shrink-0 flex items-center justify-between px-[22px] py-[14px] border-b border-[#dbe3ee]">
          <h2 className="m-0 text-[#1e293b] text-[1.125rem] font-bold tracking-[-0.02em]">
            {isEditMode ? "Edit Background" : "Add New Background"}
          </h2>
          <button
            type="button"
            className="text-[#64748b] hover:text-[#1e293b] hover:bg-[#f1f5f9] rounded p-0 w-6 h-6 flex items-center justify-center transition-colors cursor-pointer border-0 bg-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleCancel}
            disabled={isLoading}
          >
            <MdClose size={18} />
          </button>
        </div>

        {/* Modal Form */}
        <form ref={formRef} onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <div className={styles.formFields}>
            {/* Background Details */}
            <div className={styles.sectionHeader}>
              <MdWallpaper className={styles.sectionHeaderIcon} />
              <span className={styles.sectionTitle}>
                {isEditMode ? "Edit Details" : "Background Details"}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3.5">
              {/* Name Field (spans full width) */}
              <div className="col-span-2 flex flex-col gap-1.5">
                <div className="flex justify-between items-center mb-0.5">
                  <label htmlFor="sName" className="text-[11px] font-semibold text-[#334155] mb-0">
                    Name <span className="text-[#ef4444] text-xs">*</span>
                  </label>
                  <span className="text-[11px] text-[#64748b] text-right">
                    {formData.sName.length}/100
                  </span>
                </div>
                <input
                  type="text"
                  id="sName"
                  name="sName"
                  value={formData.sName}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  placeholder="Enter background name"
                  maxLength="100"
                  className={`${styles.input} ${errors.sName ? styles.inputError : ""}`}
                  disabled={isLoading}
                />
                {errors.sName && (
                  <span className="text-[#ef4444] text-[10px] leading-tight flex items-center gap-1">
                    {errors.sName}
                  </span>
                )}
              </div>

              {/* Gender Field */}
              <div className="col-span-2 sm:col-span-1 flex flex-col gap-1.5">
                <label htmlFor="gender" className="text-[11px] font-semibold text-[#334155]">
                  Gender <span className="text-[#ef4444] text-xs">*</span>
                </label>
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  className={`${styles.select} ${errors.gender ? styles.inputError : ""}`}
                  disabled={isLoading}
                >
                  <option value="">Select Gender</option>
                  <option value="men">Men</option>
                  <option value="women">Women</option>
                  <option value="unisex">Unisex</option>
                </select>
                {errors.gender && (
                  <span className="text-[#ef4444] text-[10px] leading-tight flex items-center gap-1">
                    {errors.gender}
                  </span>
                )}
              </div>
            </div>

            {/* Image Uploads */}
            <div className="flex flex-col gap-1 mb-2">
              <div className={styles.sectionHeaderSecond}>
                <MdOutlineImage className={styles.sectionHeaderIcon} />
                <span className={styles.sectionTitle}>Image Uploads</span>
              </div>
              <ul className="m-0 pl-4 flex flex-col gap-0.5 list-disc text-[11px] text-slate-500 leading-normal">
                <li>
                  Allowed formats: <strong className="text-slate-700">JPG, JPEG, PNG</strong> (max 50MB).
                </li>
                <li>
                  File name must match background name (<code className="bg-slate-100 px-1 py-0.5 rounded text-primary text-[10.5px] font-semibold">&lt;name&gt;.jpg</code>).
                </li>
              </ul>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <div className="min-h-[185px] h-auto p-2 border border-dashed border-[#cbd5e1] rounded-lg bg-[#f8fafc] hover:bg-[#f1f5f9] hover:border-[#1a2030] flex flex-col items-center justify-start relative transition-all duration-200">
                <span className="text-[9px] font-bold text-[#64748b] tracking-[0.05em] uppercase mb-1.5">
                  Background Image <span className="text-[#ef4444] text-xs">*</span>
                </span>
                <input
                  type="file"
                  id="backgroundImageModal"
                  accept=".jpg,.jpeg,.png"
                  onChange={handleImageChange}
                  className="hidden"
                  disabled={isLoading}
                />
                {previewUrl ? (
                  <div className="relative w-full flex flex-col items-center mt-1">
                    <img
                      src={previewUrl}
                      alt="Background preview"
                      className="w-full h-20 object-contain rounded bg-white cursor-pointer"
                      onClick={() => {
                        if (!isLoading) {
                          document.getElementById("backgroundImageModal")?.click();
                        }
                      }}
                      title="Click to choose a new file"
                    />
                    <button
                      type="button"
                      className="absolute top-1 right-1 w-[18px] h-[18px] rounded-full bg-slate-900/60 hover:bg-slate-900/85 text-white flex items-center justify-center cursor-pointer text-[10px] transition-colors z-10 border-0 p-0"
                      onClick={handleRemoveImage}
                      disabled={isLoading}
                      title="Remove image"
                    >
                      <MdClose size={12} />
                    </button>
                    <div className="flex flex-col items-center gap-px mt-1 w-full">
                      <span className="text-[9.5px] font-medium text-[#10b981] truncate max-w-full text-center">
                        {formData.image
                          ? `Uploaded: ${formData.image.name}`
                          : `Current: ${formData.sName || "Background"}`}
                      </span>
                      {formData.image && (
                        <span className="text-[8.5px] text-[#94a3b8]">
                          {(formData.image.size / (1024 * 1024)).toFixed(2)} MB
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  <label
                    htmlFor="backgroundImageModal"
                    className="flex flex-col items-center justify-center gap-1 w-full flex-1 p-2 bg-transparent text-slate-500 cursor-pointer text-center group"
                  >
                    <div className="w-9 h-9 rounded-full bg-slate-100 group-hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors mb-1 shadow-sm">
                      <MdCloudUpload size={20} />
                    </div>
                    <span className="font-semibold text-slate-700 text-xs leading-tight">
                      Upload image
                    </span>
                    <span className="text-[10px] text-slate-400 mt-0.5 leading-tight">
                      JPG, JPEG, PNG • Max 50MB
                    </span>
                    <span className="text-[9.5px] font-medium text-slate-600 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full mt-1.5 max-w-[95%] truncate leading-tight">
                      Format: &lt;name&gt;.jpg
                    </span>
                  </label>
                )}
                {errors.image && (
                  <span className="text-[#ef4444] text-[10px] leading-tight mt-1 flex items-center gap-1">
                    {errors.image}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Action Footer */}
          <div className="shrink-0 flex justify-end gap-2 px-[22px] py-3 border-t border-[#dbe3ee] bg-white">
            {isFormDirty && (
              <button
                type="button"
                className="mr-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-[11.5px] font-semibold text-[#475569] bg-white border border-[#cbd5e1] hover:bg-[#f8fafc] hover:border-[#94a3b8] rounded shadow-sm transition-all cursor-pointer active:scale-98 disabled:opacity-50"
                onClick={handleReset}
                disabled={isLoading}
                title="Reset form"
              >
                <MdRestartAlt size={14} />
                <span>Reset</span>
              </button>
            )}
            <button
              type="button"
              className="px-3 py-1.5 text-[11.5px] font-semibold text-[#475569] bg-white border border-[#cbd5e1] hover:bg-[#f8fafc] hover:border-[#94a3b8] rounded shadow-sm transition-all cursor-pointer active:scale-98 disabled:opacity-50"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-3 py-1.5 text-[11.5px] font-semibold text-white bg-[#1a2030] border border-[#1a2030] hover:bg-[#0f172a] rounded shadow-sm transition-all cursor-pointer active:scale-98 disabled:bg-[#e2e8f0] disabled:text-[#94a3b8] disabled:border-[#e2e8f0] disabled:cursor-not-allowed disabled:shadow-none"
              disabled={isLoading || !isFormValid || (isEditMode && !hasChanges)}
            >
              {isLoading
                ? "Saving..."
                : (isEditMode ? "Save Changes" : "Save")}
            </button>
          </div>
        </form>
      </div>

      {zoomImage && (
        <div
          className="fixed inset-0 bg-black/95 flex items-center justify-center z-[2000]"
          onClick={() => setZoomImage(null)}
        >
          <div
            className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={zoomImage}
              alt="Zoomed"
              className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
            />
            <button
              type="button"
              className="absolute top-5 right-5 w-11 h-11 rounded-full border border-white/30 bg-black/50 hover:bg-black/80 hover:border-white/60 text-white text-[28px] cursor-pointer flex items-center justify-center transition-all backdrop-blur-md"
              onClick={() => setZoomImage(null)}
            >
              <MdClose />
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default BackgroundUploadModal;